import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import type { Node as PMNode } from '@tiptap/pm/model';
import {
  Copy,
  GripVertical,
  Heading1,
  Heading2,
  List,
  ListChecks,
  Pilcrow,
  Trash2,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type * as Y from 'yjs';
import { createEditorExtensions } from './extensions';
import { EditorHostContext, type EditorHost } from './host';
import { FormatBar, TableBar } from './FormatBar';

export interface NexusEditorProps {
  document: Y.Doc;
  host: EditorHost;
  /** Called once the editor exists (for source mode, version restore, …). */
  onReady?: (editor: Editor) => void;
  autoFocus?: boolean;
  handlePaste?: (editor: Editor, event: ClipboardEvent) => boolean;
  /** No typing, no drag handle, no block/format menus -- a read-only workspace membership. */
  readOnly?: boolean;
}

/** Block menu behind the drag handle: turn into, duplicate, delete. */
function BlockMenu({
  editor,
  target,
  onClose,
  t,
}: {
  editor: Editor;
  target: { node: PMNode; pos: number };
  onClose: () => void;
  t: (key: string) => string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const rect = (editor.view.nodeDOM(target.pos) as HTMLElement | null)?.getBoundingClientRect();
  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const select = () => editor.chain().focus().setNodeSelection(target.pos);
  const inner = () =>
    editor
      .chain()
      .focus()
      .setTextSelection(target.pos + 1);
  const actions = [
    { id: 'text', icon: <Pilcrow size={14} />, run: () => inner().setParagraph().run() },
    { id: 'h1', icon: <Heading1 size={14} />, run: () => inner().setHeading({ level: 1 }).run() },
    { id: 'h2', icon: <Heading2 size={14} />, run: () => inner().setHeading({ level: 2 }).run() },
    { id: 'bullets', icon: <List size={14} />, run: () => inner().toggleBulletList().run() },
    { id: 'tasks', icon: <ListChecks size={14} />, run: () => inner().toggleTaskList().run() },
    {
      id: 'duplicate',
      icon: <Copy size={14} />,
      run: () =>
        editor
          .chain()
          .focus()
          .insertContentAt(target.pos + target.node.nodeSize, target.node.toJSON())
          .run(),
    },
    { id: 'delete', icon: <Trash2 size={14} />, run: () => select().deleteSelection().run() },
  ];
  return (
    <div
      ref={ref}
      className="nx-menu nx-block-menu"
      style={{ left: (rect?.left ?? 0) - 8, top: (rect?.top ?? 0) + 24 }}
      role="menu"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          const buttons = [...(ref.current?.querySelectorAll('button') ?? [])];
          const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
          buttons[
            (i + (e.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length
          ]?.focus();
          e.preventDefault();
        }
      }}
    >
      {actions.map((a) => (
        <button
          key={a.id}
          type="button"
          role="menuitem"
          className="nx-menu-item"
          onClick={() => {
            a.run();
            onClose();
          }}
        >
          <span className="nx-menu-icon">{a.icon}</span>
          <span className="nx-menu-label">{t(`editor.block.${a.id}`)}</span>
        </button>
      ))}
    </div>
  );
}

type Latest = React.RefObject<{ host: EditorHost; handlePaste: NexusEditorProps['handlePaste'] }>;

/** A host whose members always resolve to the latest host passed to the editor. */
function forwardingHost(latest: Latest): EditorHost {
  return new Proxy({} as EditorHost, {
    get: (_t, key: keyof EditorHost) => latest.current.host[key],
  });
}

const pasteHandler = (latest: Latest) => latest.current.handlePaste;

export function NexusEditor({
  document,
  host,
  onReady,
  autoFocus,
  handlePaste,
  readOnly,
}: NexusEditorProps) {
  // Extensions are built once per document. The latest host and paste handler are
  // read through a ref, so re-renders of the app never rebuild the editor.
  const latest = useRef({ host, handlePaste });
  useLayoutEffect(() => {
    latest.current = { host, handlePaste };
  });
  // The ref is only dereferenced later, when the editor calls into the host.
  // eslint-disable-next-line react-hooks/refs
  const [stableHost] = useState(() => forwardingHost(latest));
  const extensions = useMemo(
    () =>
      createEditorExtensions({
        document,
        host: stableHost,
        placeholder: stableHost.t('editor.placeholder'),
      }),
    [document, stableHost],
  );
  const editor = useEditor(
    {
      extensions,
      editable: !readOnly,
      autofocus: autoFocus ? 'end' : false,
      editorProps: {
        attributes: {
          class: 'nx-prose nx-editor',
          spellcheck: 'true',
          'aria-label': stableHost.t('editor.label'),
          'aria-multiline': 'true',
        },
        handlePaste: (view, event) => {
          // TipTap exposes the editor on its root element.
          const ed = (view.dom as unknown as { editor?: Editor }).editor;
          return !!(ed && pasteHandler(latest)?.(ed, event));
        },
      },
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
    },
    [extensions, readOnly],
  );
  const [menuFor, setMenuFor] = useState<{ node: PMNode; pos: number } | null>(null);
  const hovered = useRef<{ node: PMNode; pos: number } | null>(null);

  useEffect(() => {
    if (editor) onReady?.(editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;
  return (
    <EditorHostContext.Provider value={stableHost}>
      <div className="nx-editor-wrap">
        {!readOnly && (
          <DragHandle
            editor={editor}
            onNodeChange={({ node, pos }) => {
              hovered.current = node ? { node, pos } : null;
            }}
          >
            <button
              type="button"
              className="nx-drag-handle"
              aria-label={host.t('editor.blockMenu')}
              onClick={() => setMenuFor(hovered.current)}
            >
              <GripVertical size={16} />
            </button>
          </DragHandle>
        )}
        {!readOnly && menuFor && (
          <BlockMenu
            editor={editor}
            target={menuFor}
            onClose={() => setMenuFor(null)}
            t={(k) => host.t(k)}
          />
        )}
        {!readOnly && <FormatBar editor={editor} />}
        {!readOnly && <TableBar editor={editor} />}
        <EditorContent editor={editor} />
      </div>
    </EditorHostContext.Provider>
  );
}
